import React, { useState } from "react";
// import MainImage from "../../assets/HeroImages/MainPot10.jpg";
import MainImage from "../../../assets/HeroImages/MainPot10.jpg";
import Footer from "../../../components/Footer";
import Navbar from "../../../components/Navbar";
import PastWork from "../../../components/PastWord"; // Assuming this component exists
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"; // Import icons
import FirstStep from "../../../components/FirstStep";
import About from "../../../components/About";
import FeatureImage from "../../../assets/Dr.Naomi Website.webp";

function AccordionItem({ title, children, isOpen, onToggle }) {
  const firstSentence = React.Children.toArray(children).find(
    (child) => typeof child === "string" || (React.isValidElement(child) && child.type === "p")
  );

  const previewText =
    typeof firstSentence === "string"
      ? firstSentence.split(".")[0] + "."
      : React.isValidElement(firstSentence) && firstSentence.type === "p"
      ? React.Children.toArray(firstSentence.props.children)
          .join("")
          .split(".")[0] + "."
      : "";

  const restOfChildren = React.Children.toArray(children).slice(
    firstSentence ? 1 : 0
  );

  return (
    <div className="bg-white rounded-lg shadow-md mb-6">
      <div className="p-6">
        <button
          type="button"
          className="flex items-center justify-between w-full text-left focus:outline-none"
          onClick={onToggle}
        >
          <h3 className="text-xl font-semibold text-neutral-800">{title}</h3>
          {isOpen ? (
            <ChevronUpIcon className="h-5 w-5 text-neutral-600" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-neutral-600" />
          )}
        </button>
        {!isOpen && previewText && (
          <p className="text-neutral-700 leading-relaxed mt-2 text-sm italic">
            {previewText}
          </p>
        )}
      </div>
      {isOpen && (
        <div className="p-6 border-t border-neutral-200 text-neutral-700">
          {firstSentence}
          {restOfChildren}
        </div>
      )}
    </div>
  );
}

function Blog1Essentials() {
  const [openItem, setOpenItem] = useState(null);

  const toggleAccordion = (title) => {
    setOpenItem(openItem === title ? null : title);
  };

  return (
    <main className="relative min-h-screen w-screen overflow-x-hidden bg-[#F7F3F2]">
      <title>Essential Website Pages for Women Entrepreneurs | Zilker Web Design</title>
      <meta
        name="description"
        content="Discover the essential website pages every successful woman entrepreneur needs to build a strong online presence and attract ideal clients."
      />
      <meta
        name="keywords"
        content="Essential Website Pages, Website for Entrepreneurs, Online Presence for Women Business Owners, Key Website Pages for Business, Must-Have Website Pages, Homepage for Small Business, Services Page Best Practices, About Us Page for Entrepreneurs, Testimonials Page for Business, Contact Page for Website, Women Entrepreneurs Website, Website for Female Founders, Online Marketing for Women-Owned Business, Austin Women Business Website, Website Tips for Women in Business, Website to Attract Clients, Build Credibility Online Website, Effective Website for Small Business, Convert Website Visitors to Customers, Professional Website for Entrepreneurs"
      />

      <section className="bg-[#F7F3F2]">
        <div
          className="relative h-auto bg-cover bg-top"
          style={{
            clipPath: "polygon(0 0, 100% 0, 100% 80%, 0 100%)",
            backgroundImage: `url(${MainImage})`,
            padding: "20px",
            width: "100%",
          }}
        >
          <Navbar />
          <div className="mx-auto w-full max-w-7xl px-5 py-16 md:px-10 md:py-20">
            <div className="flex items-center justify-center">
              <h1 className="text-black italic text-3xl max-w-96 font-semibold text-center bg-[#CFF098]">
                The Foundation of Your Online Success
              </h1>
            </div>
          </div>
        </div>

        <section className="bg-[#F7F3F2] py-16 md:py-24">
          <div className="mx-auto w-full max-w-7xl px-5 md:px-10">
            <div className="flex flex-col items-center text-center mb-16">
              <h2 className="text-3xl font-semibold md:text-4xl text-neutral-800 max-w-4xl uppercase tracking-wide">
              Essential Website Pages for Women Entrepreneurs
              </h2>
              <div className="w-16 h-1 bg-[#CFF098] mt-4 rounded-full"></div>
              <p className="mt-6 max-w-3xl text-lg text-neutral-700 font-light leading-relaxed">
                As a driven woman entrepreneur in Austin, your website is your dynamic digital hub. These essential pages are the bedrock for attracting your ideal clients and establishing a powerful online presence that truly reflects your expertise.
              </p>
            </div>

            <div className="flex flex-col gap-6 mb-12">
              <AccordionItem
                title="Homepage: Your Digital Welcome Mat"
                isOpen={openItem === "Homepage"}
                onToggle={() => toggleAccordion("Homepage")}
              >
                <p className="text-neutral-700 leading-relaxed mb-4">
                  Think of your homepage as the warm, inviting front door to your business. It often crafts that crucial first impression. A compelling homepage clearly articulates <strong className="font-semibold">who you are</strong>, <strong className="font-semibold">what you do</strong>, and, most importantly, <strong className="font-semibold">who you serve</strong>, all within those critical initial moments.
                </p>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  It should be visually engaging, incredibly easy to navigate, and intuitively guide visitors towards the most valuable information. Why is this paramount? Because it sets the tone for their entire journey, immediately captures their attention, and acts as a clear roadmap, encouraging them to explore further and discover how you can uniquely help them achieve their goals.
                </p>
                <p className="text-sm text-neutral-500 italic">
                  Essential for: Setting the tone, capturing attention, and guiding navigation.
                </p>
              </AccordionItem>

              <AccordionItem
                title="Services Page: Showcasing Your Expertise"
                isOpen={openItem === "Services Page"}
                onToggle={() => toggleAccordion("Services Page")}
              >
                <p className="text-neutral-700 leading-relaxed mb-4">
                  This page is where your expertise truly shines. It's the dedicated space to meticulously detail the <strong className="font-semibold">specific services or products you expertly offer</strong>. Clearly outline the remarkable benefits your clients will gain, walk them through your unique process step-by-step, and emphatically showcase the immense value and transformative results you consistently deliver.
                </p>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  Employ compelling language that directly resonates with their needs and aspirations, and consider incorporating high-quality visuals, such as images or videos, that further underscore your expertise and powerfully demonstrate how you can effectively solve their specific challenges.
                </p>
                <p className="text-sm text-neutral-500 italic">
                  Essential for: Informing potential clients and demonstrating your offerings.
                </p>
              </AccordionItem>

              <AccordionItem
                title="About Us/Team Page: Building Connection and Trust"
                isOpen={openItem === "About Us/Team Page"}
                onToggle={() => toggleAccordion("About Us/Team Page")}
              >
                <p className="text-neutral-700 leading-relaxed mb-4">
                  Remember, in the heart of Austin and beyond, people connect with people. Your About Us or Team Page is your invaluable opportunity to <strong className="font-semibold">share your unique story</strong>, articulate your compelling mission, clearly define your core values, and warmly introduce yourself or your talented team.
                </p>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  This personal touch helps to humanize your brand, making it relatable and approachable, ultimately establishing a strong foundation of credibility and fostering a deeper connection with your potential clients right here in our vibrant community and across the digital landscape.
                </p>
                <p className="text-sm text-neutral-500 italic">
                  Essential for: Humanizing your brand and fostering trust.
                </p>
              </AccordionItem>

              <AccordionItem
                title="Testimonials/Case Studies Page: Social Proof That Converts"
                isOpen={openItem === "Testimonials/Case Studies Page"}
                onToggle={() => toggleAccordion("Testimonials/Case Studies Page")}
              >
                <p className="text-neutral-700 leading-relaxed mb-4">
                  Don't underestimate the profound impact of social proof! Your Testimonials or Case Studies Page is where the authentic voices of your delighted clients truly shine. Nothing resonates more powerfully than the genuine positive experiences of those you've already successfully helped.
                </p>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  This dedicated page effectively showcases <strong className="font-semibold">real results and genuine positive feedback</strong>, providing undeniable social proof that you consistently deliver exceptional value and achieve remarkable outcomes. For an even greater impact, case studies can delve into specific projects, illustrating the challenges, your strategic approach, and the significant positive results you achieved for your clients.
                </p>
                <p className="text-sm text-neutral-500 italic">
                  Essential for: Building credibility and influencing potential clients.
                </p>
              </AccordionItem>

              <AccordionItem
                title="Contact Page: Making it Easy to Connect"
                isOpen={openItem === "Contact Page"}
                onToggle={() => toggleAccordion("Contact Page")}
              >
                <p className="text-neutral-700 leading-relaxed mb-4">
                  Finally, ensure a seamless pathway for those eager clients to reach out. Your Contact Page is the crucial gateway for turning intrigued visitors into valuable leads. Provide multiple clear and easily accessible ways for people to get in touch with you, such as a user-friendly <strong className="font-semibold">contact form</strong>, a direct and monitored <strong className="font-semibold">email address</strong>, and a readily available <strong className="font-semibold">phone number</strong>.
                </p>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  If it aligns with your business, consider also including your business hours or physical location right here in Austin, making it even easier for local clients to connect. Why is this page absolutely essential? Because it provides a direct and frictionless line of communication, ensuring that you never miss out on potential opportunities to connect with and serve your ideal clients.
                </p>
                <p className="text-sm text-neutral-500 italic">
                  Essential for: Generating leads and facilitating communication.
                </p>
              </AccordionItem>
            </div>

            {/* Authority Launchpad Section */}
            <div className="mt-16 py-12 rounded-lg shadow-md p-8 bg-white border border-neutral-200 relative overflow-hidden">
              <h2 className="text-2xl font-semibold text-neutral-800 text-center mb-6 uppercase tracking-wide">
                Ready to Launch Your Authority Online?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-neutral-700 mb-4">The Authority Launchpad</h3>
                  <p className="text-neutral-700 font-light leading-relaxed mb-6">
                    Establish a robust foundational online presence and build unwavering credibility with our specifically tailored Authority Launchpad package.
                  </p>
                  <p className="text-neutral-600 font-semibold mb-2">
                    Best For:
                  </p>
                  <p className="text-neutral-700 leading-relaxed mb-4">
                    Newer businesses or those with a basic website seeking a significant upgrade to accurately reflect their expertise and attract their ideal clientele.
                  </p>
                  <ul className="list-disc list-inside mt-4 text-neutral-700 leading-relaxed">
                    <li className="mb-2"><strong className="font-semibold">Elite Website Design:</strong> A professionally designed, up to 5-page website (Homepage, Services, About Us/Team, Testimonials/Case Studies, Contact) meticulously tailored to showcase your unique services and powerfully reflect your distinct brand identity.</li>
                    <li className="mb-2"><strong className="font-semibold">Foundational SEO Setup:</strong> Implementation of basic yet crucial on-page optimization for target keywords most relevant to your core services and your local Austin area. Includes essential meta descriptions, effective title tags, and initial in-depth keyword research.</li>
                    <li className="mb-2"><strong className="font-semibold">Mobile-Responsive Design:</strong> Ensuring your website delivers a seamless and visually appealing experience across all devices, from smartphones to tablets to desktops.</li>
                    <li className="mb-2"><strong className="font-semibold">High-Quality Content Integration:</strong> Expert guidance and valuable support in strategically incorporating your existing content or crafting compelling new content that directly speaks to your ideal clients' needs and aspirations.</li>
                    <li className="mb-2"><strong className="font-semibold">Professional Stock Imagery:</strong> Access to our curated library of high-quality, professional images to elevate the visual appeal and professionalism of your online presence.</li>
                    <li><strong className="font-semibold">Basic Analytics Setup:</strong> Seamless integration with Google Analytics to enable you to track your website's performance from the outset.</li>
                  </ul>
                  <a href="/LaunchGlowUp" className="inline-block mt-8 bg-[#CFF098] text-black py-3 px-6 text-sm rounded-full transition duration-300 focus:outline-none focus:ring-2 focus:ring-[#CFF098] focus:ring-offset-1">
                    Learn More About the Authority Launchpad
                  </a>
                </div>
                <div className="relative flex items-center justify-center">
                  {/* Image */}
                  <div className="absolute inset-y-0 right-0 w-1/2 md:w-auto">
                    <img
                      src={FeatureImage}
                      alt="Authority Launchpad"
                      className="h-full object-cover object-[15%_75%]"
                    />
                  </div>
                  {/* Empty div to maintain layout on smaller screens */}
                  <div className="hidden md:block w-64"></div>
                </div>
              </div>
            </div>

            {/* Other Packages (Briefly Mentioned) */}
            <div className="mt-16 text-center">
              <h2 className="text-2xl font-semibold text-neutral-800 mb-6 uppercase tracking-wide">
                Ready to Scale and Amplify Your Authority?
              </h2>
              <p className="text-lg text-neutral-700 leading-relaxed mb-8">
                For ambitious entrepreneurs ready to drive targeted traffic, generate high-quality leads, and firmly establish themselves as leading authorities in their niche, our <a href="/strategicgrowthaccelerator" className="font-semibold hover:underline italic bg-[#CFF098]">Strategic Growth Accelerator</a> and premium <a href="/authorityamplifier" className="font-semibold hover:underline italic bg-[#CFF098]">Authority Amplifier</a> packages offer advanced and comprehensive solutions.
              </p>
              <a href="/services" className="inline-block bg-[#CFF098] text-black py-3 px-6 text-sm rounded-full transition duration-300 focus:outline-none focus:ring-2 focus:ring-[#CFF098] focus:ring-offset-1">
                Explore All Packages
              </a>
            </div>
          </div>
        </section>
        <div className="bg-[#2E2D2B]">
          <About />
        </div>
        <Footer />
      </section>
    </main>
  );
}

export default Blog1Essentials;